import AdminDashboardLayout from '../../../layouts/AdminDashboardLayout';
import EmailConfigurationForm from './modules/EmailConfigurationForm';
import type { PreviewData } from './modules/EmailPreview';
import EmailPreview from './modules/EmailPreview';
import React, { useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import EmailsTable from './modules/EmailsTable';
import type { WeeklyLeadersEmail } from '@whizz/api-model';
import { DateTime } from 'luxon';
import { getNextWeekday, getPrevWeekday } from '../../../util/dates';

export default function WeeklyLeadersEmailsPageComponent() {
  const { translate: t } = useI18n();

  const [ weeklyLeadersEmail, setWeeklyLeadersEmail ] = useState<WeeklyLeadersEmail | null>(null);
  const [ previewData, setPreviewData ] = useState<PreviewData>({
    isEnabled: false,
    school: null,
    startDate: null,
    endDate: null,
    teachers: [],
    standardGreeting: true,
    message: null,
    reminderMessage: null,
    metricsComponents: [],
    studentsData: [],
  });

  function content() {
    if (null !== weeklyLeadersEmail) {
      return (
        <div className={'grid grid-cols-2 gap-10'}>
          <div>
            <EmailConfigurationForm
              weeklyLeadersEmail={weeklyLeadersEmail}
              setPreviewData={setPreviewData}
              onBackToTable={() => {
                setWeeklyLeadersEmail(null);
                scroll(0, 0);
              }}
            />
          </div>
          <div>
            <EmailPreview
              previewData={previewData}
            />
          </div>
        </div>
      );
    }

    return (
      <EmailsTable
        onClickEdit={(weeklyLeadersEmail) => {
          setWeeklyLeadersEmail(weeklyLeadersEmail);

          // Preview
          let dateFrom = DateTime.now().setZone(weeklyLeadersEmail.school.timezone);

          if (null != weeklyLeadersEmail.pausedUntil) {
            const pausedUntil = DateTime.fromISO(weeklyLeadersEmail.pausedUntil);

            if (dateFrom < pausedUntil) {
              dateFrom = pausedUntil;
            }
          }

          let nextWeekday = null;
          let reportingPeriodFromDate: DateTime | null = null;
          let reportingPeriodToDate: DateTime | null = null;

          if (null != weeklyLeadersEmail.weekday && null !== weeklyLeadersEmail.hour) {
            nextWeekday = getNextWeekday(weeklyLeadersEmail.weekday, weeklyLeadersEmail.hour, dateFrom);

            if (null != weeklyLeadersEmail.reportingPeriodFrom && null != weeklyLeadersEmail.reportingPeriodTo) {
              reportingPeriodToDate = getPrevWeekday(weeklyLeadersEmail.reportingPeriodTo, 23, nextWeekday);
              reportingPeriodFromDate = getPrevWeekday(weeklyLeadersEmail.reportingPeriodFrom, 0, reportingPeriodToDate);
            }
          }

          setPreviewData({
            isEnabled: weeklyLeadersEmail.isEnabled ?? false,
            school: weeklyLeadersEmail.school,
            startDate: reportingPeriodFromDate ?? null,
            endDate: reportingPeriodToDate ?? null,
            teachers: weeklyLeadersEmail.teachers.map((teacher) => teacher.firstname),
            standardGreeting: weeklyLeadersEmail.standardGreeting ?? true,
            message: weeklyLeadersEmail.isEnabledOneOffMessage ? weeklyLeadersEmail.oneOffMessage : weeklyLeadersEmail.standardMessage,
            reminderMessage: weeklyLeadersEmail.isEnabledLearningOutcomeReminderMessage ? weeklyLeadersEmail.learningOutcomeReminderMessage : null,
            metricsComponents: weeklyLeadersEmail.metricsComponents ?? [],
            studentsData: [],
          });

          scroll(0, 0);
        }}
      />
    );
  }

  return (
    <AdminDashboardLayout
      title={t('pages.ADMIN.HEADER')}
    >
      {content()}
    </AdminDashboardLayout>
  );
}
